/**
 * Wiring between the DocStore and the editor store (W1-09 state-store; ARCHITECTURE.md §5.3-§5.5).
 *
 * `connectDocStore(store, editor)` (called by W1-16 when a project opens):
 *  - installs the SelectionBridge: transactions capture the editor selection and active line before and after,
 *    and undo/redo restore them (DESIGN.md §5.13: "Undo restores the selection of that moment");
 *  - after every change that removed records (deletes, undo of an insert, load), drops references to them:
 *    `Sel.prune` on the selection; the active line (falls back to the line now at its old export index, else the
 *    nearest one); the focused column, pinned columns, pinned lanes and "also show" lines of deleted lines; hold and
 *    segment placements on deleted lines; a hover target that is gone. Preview filters keep their ids (ids are
 *    never reused, and an undo brings the line back with its filters).
 */

import type { Unsubscribe } from '../core/util/types';
import type { ChartDoc, LineId } from '../core/model/types';
import type { ChangeSet, SelectionSnapshot } from '../core/ops/types';
import { DETACHED_BRIDGE } from './docStore';
import { editorStore } from './editorStore';
import { Sel } from './selection';
import type { DocStore, EditorStore, EditorStoreApi, SelectionBridge } from './types';

export interface ConnectOptions {
  /**
   * Whether undo/redo (and commands that set it) may change the active line to the one recorded in the selection
   * snapshot (setting "Jump to change on undo", default on).
   */
  readonly restoreActiveLine?: () => boolean;
}

/** A SelectionBridge over an editor store. */
export function editorSelectionBridge(
  store: DocStore,
  editor: EditorStoreApi,
  opts: ConnectOptions = {},
): SelectionBridge {
  return {
    get: (): SelectionSnapshot => {
      const s = editor.getState();
      return Sel.toSnapshot(s.selection, s.activeLine);
    },
    set: (snap: SelectionSnapshot): void => {
      const s = editor.getState();
      const doc = store.doc;
      // Snapshots carry no primary or anchor: the current ones stay when they are still selected (the inspector keeps
      // its item across an undo that restores the same selection), else the primary is inferred.
      const restored = Sel.prune(Sel.fromSnapshot(snap), doc);
      const primary = s.selection.primary;
      const anchor = s.selection.anchor;
      s.select(
        {
          ...restored,
          ...(primary !== null && Sel.holds(restored, primary) ? { primary } : {}),
          ...(anchor !== null && Sel.holds(restored, anchor) ? { anchor } : {}),
        },
        'replace',
      );
      const line = snap.activeLine;
      if (line !== null && line !== s.activeLine && doc.lines.has(line) && (opts.restoreActiveLine?.() ?? true))
        s.setActiveLine(line);
    },
  };
}

function removesRecords(cs: ChangeSet): boolean {
  if (cs.source === 'load') return true;
  for (const op of cs.ops) if (op.o === 'del') return true;
  return false;
}

/** The line that replaces a deleted active line: the one now at its old export index (or the last), else null. */
function replacementLine(cs: ChangeSet, doc: ChartDoc, gone: LineId): LineId | null {
  const order = doc.lineOrder;
  if (order.length === 0) return null;
  let oldIndex = -1;
  for (const op of cs.ops) {
    if (op.o === 'order' && op.list === 'lineOrder') {
      const i = op.prev.indexOf(gone);
      if (i >= 0) {
        oldIndex = i;
        break;
      }
    }
  }
  if (oldIndex < 0) return order[0] ?? null;
  return order[Math.min(oldIndex, order.length - 1)] ?? null;
}

/** Drops editor references to records the document no longer has. */
export function pruneEditor(editor: EditorStoreApi, doc: ChartDoc, cs: ChangeSet | null = null): void {
  const s: EditorStore = editor.getState();
  const has = (id: LineId): boolean => doc.lines.has(id);

  const sel = Sel.prune(s.selection, doc);
  if (sel !== s.selection) s.select(sel, 'replace');

  if (s.activeLine !== null && !has(s.activeLine)) {
    const next = cs === null ? (doc.lineOrder[0] ?? null) : replacementLine(cs, doc, s.activeLine);
    s.setActiveLine(next);
  }
  const cur = editor.getState();
  if (cur.focusedColumn !== null && !has(cur.focusedColumn.line)) cur.setFocusedColumn(null);
  if (cur.pinnedColumns.some((c) => !has(c.line))) cur.setPinnedColumns(cur.pinnedColumns.filter((c) => has(c.line)));
  if (cur.pinnedLanes.some((l) => !has(l))) cur.setPinnedLanes(cur.pinnedLanes.filter(has));
  if (cur.alsoShow.some((l) => !has(l))) cur.setAlsoShow(cur.alsoShow.filter(has));
  const p = cur.placement;
  if (p !== null && !has(p.kind === 'paste' ? p.targetLine : p.line)) cur.setPlacement(null);
  const h = cur.hover;
  if (h !== null && (h.kind === 'note' ? !doc.notes.has(h.note) : !has(h.line))) cur.setHover(null);
}

/**
 * Connects a DocStore to the editor store (selection bridge + pruning). Returns the disconnect function, which
 * detaches the bridge (the store then sees an empty selection).
 */
export function connectDocStore(
  store: DocStore,
  editor: EditorStoreApi = editorStore,
  opts: ConnectOptions = {},
): Unsubscribe {
  const bridge = editorSelectionBridge(store, editor, opts);
  store.setSelectionBridge(bridge);
  const off = store.subscribe((cs) => {
    if (removesRecords(cs)) pruneEditor(editor, store.doc, cs);
  });
  let active = true;
  return () => {
    if (!active) return;
    active = false;
    off();
    store.setSelectionBridge(DETACHED_BRIDGE);
  };
}
